import { Component, useCallback, useState } from 'react';
import { Portal } from 'react-portal';
import styled from 'styled-components';

import { H3, Input, Spinner } from '@comeon/ui';

import CheckBox from '~/components/forms/checkbox';
import ConfigurationContext from '~/configuration/configuration-context';
import useLockCurrentContextScroll from '~/hooks/use-lock-current-context-scroll';
import getDevice from '~/libs/deviceDetector';
import { removeTransformNone, setTransformNone } from '~/utils/layout-utils';

// the wrapping 'div' has listeners to catch swipe-events from propagation to
// parent elements to block any underlying content to interact when the
// overlay is present. For example the 'react-slick' component adds listeners
// to the body-element. When these listeners are added to a static element
// some linterrors are triggered, and therefore disabled:
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
const onMouseMove = e => {
	e.stopPropagation();
};
const onTouchEnd = e => {
	e.stopPropagation();
};
const onTouchMove = e => {
	e.stopPropagation();
};
const onTouchStart = e => {
	e.stopPropagation();
};
const onMouseDown = e => {
	e.stopPropagation();
};
const onMouseUp = e => {
	e.stopPropagation();
};

const DEVICE_TYPE = getDevice();
const isMobile = DEVICE_TYPE === 'Mobile';
const isTablet = DEVICE_TYPE === 'Tablet';

const MOBILE_SET_TRANSFORM = 'main-layout';
const TABLET_SET_TRANSFORM = 'header-bigger';

const TRANSFORM_ELEMENTS = {
	Mobile: MOBILE_SET_TRANSFORM,
	Tablet: TABLET_SET_TRANSFORM
};

const enableTransformNone = () => {
	setTransformNone(TRANSFORM_ELEMENTS[DEVICE_TYPE]);
};

const disableTransformNone = () => {
	removeTransformNone(TRANSFORM_ELEMENTS[DEVICE_TYPE]);
};
const CustomCheckbox = styled(CheckBox)`
	margin-top: 24px;
`;

const validateInput = (input, min, max) => {
	/*
		based on the validation props(min, max, maxlength), "isInputInvalid" get filled
	*/
	return !(!input || (min && min > input) || (max && max < input));
};

const LockCurrentContextScrollHelper = () => {
	useLockCurrentContextScroll();
	return <></>; // eslint-disable-line react/jsx-no-useless-fragment
};

export const ButtonWithLoading = ({ hasLoading = false, onClick, children, ...other }) => {
	const [isLoading, setIsLoading] = useState(false);

	const internalOnClick = useCallback(
		event => {
			setIsLoading(true);
			onClick(event);
		},
		[setIsLoading, onClick]
	);

	return (
		<button onClick={internalOnClick} {...other}>
			{isLoading && hasLoading ? <Spinner /> : children}
		</button>
	);
};

export const Wrapper = ({ onClick, children, forwadedRef = null, className, lockContextScroll }) => {
	return (
		<div
			className={className}
			onMouseMove={onMouseMove}
			onTouchEnd={onTouchEnd}
			onTouchMove={onTouchMove}
			onTouchStart={onTouchStart}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			ref={forwadedRef}
			onClick={onClick}
		>
			{lockContextScroll && <LockCurrentContextScrollHelper />}
			{children}
		</div>
	);
};

class BinaryQuestion extends Component {
	// eslint-disable-next-line react/static-property-placement
	static contextType = ConfigurationContext;

	state = {
		inputValue: ' ',
		touched: false,
		isValid: !this.props.enableInputValidation || false,
		disableSidebarScroll: this.props.disableSidebarScroll
	};

	componentDidMount() {
		this.setState({
			isValid:
				!this.props.enableInputValidation ||
				validateInput(this.props.inputValue, this.props.min, this.props.max)
		});
	}

	componentDidUpdate() {
		if (this.state.disableSidebarScroll) {
			if (this.props.shouldOpenBinary) {
				if (isMobile || isTablet) {
					enableTransformNone();
				}
			} else if (isMobile || isTablet) {
				disableTransformNone();
			}
		}
	}

	static getDerivedStateFromProps(props, state) {
		if (!state.touched) {
			if (props.deriveInputStateFromProp && props.inputValue === state.inputValue) {
				return null;
			}
			return {
				inputValue: props.inputValue,
				touched: true,
				isValid: !props.enableInputValidation || validateInput(props.inputValue, props.min, props.max)
			};
		}
		return null;
	}

	onYesClick = e => {
		if (this.props.deriveInputStateFromProp) {
			this.props.onYesClick(this.state.inputValue);
		} else if (this.props.onYesClick) {
			this.props.onYesClick(e, this.props.inputValue);
		}
	};

	onChange = e => {
		const maxlengthExceeded = this.props.maxlength && this.props.maxlength < e.currentTarget.value.length;
		const { value } = e.currentTarget;

		if (!maxlengthExceeded) {
			/* I check maxLength here because If the value of the type attribute is text, email, search, password, tel, or url,
			this attribute specifies the maximum number of characters (in Unicode code points) that the user can enter;
			 for other control types, it is ignored. */
			const isValid =
				!this.props.enableInputValidation ||
				(validateInput(value, this.props.min, this.props.max) &&
					(this.props.customValidate ? this.props.customValidate(value) : true));
			const newState = {
				isValid
			};
			if (this.props.deriveInputStateFromProp) {
				newState.inputValue = value;
				e.persist();
			} else if (this.props.onChange) {
				this.props.onChange(e);
			}
			this.setState(() => newState);
		}
	};

	render() {
		const {
			shouldOpenBinary,
			title,
			id,
			text,
			onYesClick,
			yesText,
			onNoClick,
			noText,
			hasInput = false,
			inputValue,
			inputType,
			inputPattern,
			onOutsideClick,
			renderInParent,
			overlayParent,
			fixedPosition,
			max,
			min,
			placeholder,
			QRCodedata = false,
			signUpUrl = false,
			signUpText,
			appUrlText,
			transactionLoader = null,
			validationMessage,
			openInSidebar = false,
			stickyPos = false,
			className = '',
			inputLabel,
			children,
			reverseOrder,
			buttonsHasUncontrolledLoading = false,
			Icon,
			disableYesClick,
			thirdText,
			thirdKeyClick,
			forwadedRef
		} = this.props;
		if (!shouldOpenBinary) {
			return false;
		}

		let binaryClasses = `binary-question u-flex u-fxb-c u-align-start notify notify--is-open ${className}`;
		if (overlayParent && !openInSidebar) {
			binaryClasses = `${binaryClasses} global-notification global-notification--is-open`;
		}
		if (fixedPosition) {
			binaryClasses = `${binaryClasses} binary-question--fixed`;
		}
		if (stickyPos && !isTablet) {
			binaryClasses = `${binaryClasses} binary-question--sticky u-oa`;
		}
		const lockContextScroll = this.state.disableSidebarScroll && this.props.shouldOpenBinary;

		const binaryQuestionContent = (
			<Wrapper
				className={binaryClasses}
				forwadedRef={forwadedRef}
				lockContextScroll={lockContextScroll}
				onClick={
					onOutsideClick
						? event => {
								if (event.target.classList.contains('binary-question')) onOutsideClick();
							}
						: () => {}
				}
			>
				<div className={`binary-question__inner u-3-4-width u-b-radius ${stickyPos ? 'sticky' : ''}`}>
					<div className="binary-question__head u-p2 u-text-c">
						{Icon && Icon}
						{transactionLoader && <div>{transactionLoader}</div>}
						<H3 className="binary-question__main-title u-mb1">{title}</H3>
						<p className="js-text binary-question__p u-mb1">{text}</p>
						{hasInput &&
							(inputType === 'checkbox' ? (
								<CustomCheckbox
									id={id}
									label={inputLabel}
									iconPosition="left"
									onChange={this.onChange}
									validationMessage={this.state.isValid ? null : validationMessage}
									isChecked={
										this.props.deriveInputStateFromProp ? !!this.state.inputValue : !!inputValue
									}
								/>
							) : (
								<>
									{/* for password field in case of autoComplete. :')
											If this is not here then autoComplete will close the sidebar */}
									{inputType === 'password' && (
										<Input
											type="email"
											style={{
												position: 'absolute',
												width: '1px',
												height: '1px',
												margin: '-1px',
												border: '0',
												padding: '0',
												clip: 'rect(0 0 0 0)',
												overflow: 'hidden'
											}}
											onChange={() => {}}
										/>
									)}
									<Input
										autoFocus
										onChange={this.onChange}
										id={id}
										valid={this.state.isValid}
										value={this.props.deriveInputStateFromProp ? this.state.inputValue : inputValue}
										type={inputType}
										hideErrorIcon={inputType === 'password'}
										inputRef="binaryInput"
										forceFocus
										pattern={inputPattern}
										max={max}
										min={min}
										validationMessage={this.state.isValid ? null : validationMessage}
										placeholder={placeholder}
										label={inputLabel}
										autoComplete="new-password"
									/>
								</>
							))}
						{children}
					</div>
					{QRCodedata && (
						<div className="binary-question__head u-p2 u-text-c qr-code">
							<img
								src={QRCodedata}
								className=""
								alt="QRcode"
								style={{
									objectFit: 'cover'
								}}
							/>
						</div>
					)}
					{signUpUrl && (
						<div className="binary-question__head u-p2 u-text-c">
							<a
								href={signUpUrl}
								target="_blank"
								className="muchbetter-signup-link"
								rel="noopener noreferrer"
							>
								{QRCodedata ? appUrlText : signUpText}
							</a>
						</div>
					)}
					<div
						className={`binary-question__button-container u-b-radius-bottom ${
							thirdKeyClick ? 'u-center-vertically' : ''
						}`}
					>
						{thirdKeyClick && (
							<ButtonWithLoading
								type="button"
								data-at="bin-third"
								className="binary-question__third btn btn--secondary u-relative u-text-c"
								onClick={thirdKeyClick}
								hasLoading={buttonsHasUncontrolledLoading}
							>
								{thirdText}
							</ButtonWithLoading>
						)}
						{!reverseOrder && onNoClick && (
							<ButtonWithLoading
								type="button"
								data-at="bin-no"
								className={`${
									thirdKeyClick
										? 'binary-question__no btn btn--secondary u-relative u-text-c u-width-100'
										: 'binary-question__no btn btn--secondary u-relative u-text-c'
								}`}
								onClick={onNoClick}
								hasLoading={buttonsHasUncontrolledLoading}
							>
								{noText}
							</ButtonWithLoading>
						)}
						{onYesClick && (
							<ButtonWithLoading
								type="button"
								data-at="bin-yes"
								className={`${
									thirdKeyClick
										? 'binary-question__yes btn btn--secondary u-relative u-text-c u-width-100'
										: 'binary-question__yes btn btn--secondary u-relative u-text-c'
								}`}
								onClick={this.onYesClick}
								disabled={!this.state.isValid || disableYesClick}
								hasLoading={buttonsHasUncontrolledLoading}
							>
								{yesText}
							</ButtonWithLoading>
						)}
						{reverseOrder && onNoClick && (
							<ButtonWithLoading
								type="button"
								data-at="bin-no"
								className={`${
									thirdKeyClick
										? 'binary-question__no btn btn--secondary u-relative u-text-c u-width-100'
										: 'binary-question__no btn btn--secondary u-relative u-text-c'
								}`}
								onClick={onNoClick}
								hasLoading={buttonsHasUncontrolledLoading}
							>
								{noText}
							</ButtonWithLoading>
						)}
					</div>
				</div>
			</Wrapper>
		);

		if (openInSidebar) {
			// FIXME_SIDEBAR
			return <Portal node={document && document.getElementById('sidebar')}>{binaryQuestionContent}</Portal>;
		}
		return renderInParent ? (
			<>{binaryQuestionContent}</> // eslint-disable-line react/jsx-no-useless-fragment
		) : (
			<Portal>{binaryQuestionContent}</Portal>
		);
	}
}

export default BinaryQuestion;
